import { useLazyGetSessionsPresentersQuery } from '@carboncopyco/data';
import type { SessionsPresenter } from '@carboncopyco/data';
import { TextLargeParagraph } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { useDashboardProps } from '../../../DashboardContext';
import { MinimalTable } from '../../../MinimalTable';
import { TableEmptyState } from '../../../SessionBreakdownComponents';
import { ViewAllPresentersModal } from './ViewAllPresentersModal';
import { getCols } from './presentersCols';

const cols = getCols();

export const PresentersTable = ({ maxItems }: { maxItems?: number }) => {
  const [showViewAllModal, setShowViewAllModal] = useBoolean(false);
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();
  const [getSessionsPresenters, { data, isLoading, error }] =
    useLazyGetSessionsPresentersQuery();
  const { trackDashboardViewMoreClick } = useDashboardAnalytics();

  useEffect(() => {
    getSessionsPresenters({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['managed_presentation', 'play'],
    });
  }, [
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    getSessionsPresenters,
  ]);

  return (
    <>
      <MinimalTable<SessionsPresenter>
        header={
          <div className="flex items-center gap-2">
            <TextLargeParagraph
              color="text-ds-natural-600"
              label="Presenters"
            />
            {!!data?.metadata.totalItems && (
              <TextLargeParagraph
                label={`(${data?.metadata.totalItems})`}
                color="text-ds-natural-300"
              />
            )}
          </div>
        }
        error={error}
        cols={cols}
        data={data?.items ?? []}
        isLoading={isLoading}
        maxItems={maxItems}
        onViewAll={() => {
          setShowViewAllModal.setTrue();
          trackDashboardViewMoreClick({
            dashboardSection: 'presenters_table',
          });
        }}
        emptyState={
          <div className="flex flex-col items-center gap-2 px-6 py-20">
            <TableEmptyState />
          </div>
        }
      />
      {showViewAllModal && (
        <ViewAllPresentersModal
          open={showViewAllModal}
          onClose={setShowViewAllModal.setFalse}
        />
      )}
    </>
  );
};
